import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../services/authService';
import { format } from 'date-fns';

const PendingApprovalsPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: '' });

  const fetchRegistrations = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getRegistrations(filter !== 'all' ? filter : null);
      setRegistrations(data.results || data);
    } catch {
      setError('Failed to load registration requests');
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminService.approveRegistration(id);
      fetchRegistrations();
    } catch {
      setError('Failed to approve registration');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    setActionLoading(rejectModal.id);
    try {
      await adminService.rejectRegistration(rejectModal.id, rejectModal.reason);
      setRejectModal({ open: false, id: null, reason: '' });
      fetchRegistrations();
    } catch {
      setError('Failed to reject registration');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCleanup = async () => {
    setCleanupLoading(true);
    setError(null);
    try {
      const result = await adminService.cleanupRegistrations();
      setSuccessMessage(result.message || 'Cleanup completed successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchRegistrations();
    } catch {
      setError('Failed to cleanup stale registrations');
    } finally {
      setCleanupLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-warning-500/20 text-warning-400',
      approved: 'bg-success-500/20 text-success-400',
      rejected: 'bg-error-500/20 text-error-400',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Registration Requests</h1>
          <p className="text-dark-400 mt-1">Review and manage user registration requests</p>
        </div>
        <button
          onClick={handleCleanup}
          disabled={cleanupLoading}
          className="px-4 py-2 bg-dark-600 text-dark-300 rounded-lg text-sm font-medium hover:bg-dark-500 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {cleanupLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dark-300"></div>
              Cleaning...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Cleanup My Entries
            </>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-primary-500/20 text-primary-400'
                : 'text-dark-400 hover:bg-dark-700 hover:text-dark-100'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-success-500/10 border border-success-500/30 rounded-lg p-4">
          <p className="text-success-400">{successMessage}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400">{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : registrations.length === 0 ? (
        <div className="card-dark p-12 text-center">
          <svg
            className="w-16 h-16 text-dark-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p className="text-dark-400">No registration requests found</p>
        </div>
      ) : (
        <div className="card-dark overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  User
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Company
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {registrations.map((reg) => (
                <tr key={reg.id} className="hover:bg-dark-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-dark-100 font-medium">
                        {reg.first_name} {reg.last_name}
                      </p>
                      <p className="text-dark-500 text-sm">{reg.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-dark-300">{reg.company}</p>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(reg.status)}</td>
                  <td className="px-6 py-4">
                    <p className="text-dark-400 text-sm">
                      {format(new Date(reg.created_at), 'MMM d, yyyy')}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    {reg.status === 'pending' && (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleApprove(reg.id)}
                          disabled={actionLoading === reg.id}
                          className="px-3 py-1.5 bg-success-500/20 text-success-400 rounded-lg text-sm font-medium hover:bg-success-500/30 transition-colors disabled:opacity-50"
                        >
                          {actionLoading === reg.id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => setRejectModal({ open: true, id: reg.id, reason: '' })}
                          disabled={actionLoading === reg.id}
                          className="px-3 py-1.5 bg-error-500/20 text-error-400 rounded-lg text-sm font-medium hover:bg-error-500/30 transition-colors disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Reject Registration</h3>
            <p className="text-dark-400 text-sm mb-4">
              Optionally provide a reason for rejecting this registration request.
            </p>
            <textarea
              value={rejectModal.reason}
              onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
              className="input-dark w-full resize-none"
              rows={3}
              placeholder="Reason for rejection (optional)"
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setRejectModal({ open: false, id: null, reason: '' })}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading === rejectModal.id}
                className="px-4 py-2 bg-error-500 text-white rounded-lg font-medium hover:bg-error-600 transition-colors disabled:opacity-50"
              >
                {actionLoading === rejectModal.id ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApprovalsPage;
